import { Alert, Paper, SimpleGrid, Stack, TextInput, Textarea } from "@mantine/core"
import { useForm } from "@mantine/form"
import { notifications } from "@mantine/notifications"
import { forwardRef, useEffect, useImperativeHandle } from "react"
import { useTranslation } from "react-i18next"
import { getErrorMessage } from "../utils"
import { useCreateRole, useUpdateRole } from "../hooks/useRoles"
import type { Role, RoleDetail, RoleFormValues } from "../models/role"

interface Props {
  role?: Role | RoleDetail | null
  onSuccess: () => void
}

const RoleFormContent = forwardRef<{ submit: () => void }, Props>(({ role, onSuccess }, ref) => {
  const { t } = useTranslation()
  const isEdit = !!role
  const createMutation = useCreateRole()
  const updateMutation = useUpdateRole()

  const form = useForm<RoleFormValues>({
    initialValues: {
      name: "",
      description: "",
      rule_name: null,
    },
    validate: {
      name: (v) => (v?.length >= 1 ? null : t("adminRoles.validation.nameRequired")),
    },
  })

  useEffect(() => {
    if (role) {
      form.setValues({
        name: role.name,
        description: role.description ?? "",
        rule_name: role.rule_name ?? null,
      })
    }
  }, [role])

  const handleSubmit = (values: RoleFormValues) => {
    if (isEdit && role) {
      updateMutation.mutate(
        { name: role.name, values },
        {
          onSuccess: () => {
            notifications.show({
              color: "green",
              title: t("common.success"),
              message: t("adminRoles.updateSuccess"),
            })
            onSuccess()
          },
          onError: (err) => {
            notifications.show({
              color: "red",
              title: t("common.error"),
              message: getErrorMessage(err),
            })
          },
        },
      )
    } else {
      createMutation.mutate(values, {
        onSuccess: () => {
          notifications.show({
            color: "green",
            title: t("common.success"),
            message: t("adminRoles.createSuccess"),
          })
          onSuccess()
        },
        onError: (err) => {
          notifications.show({
            color: "red",
            title: t("common.error"),
            message: getErrorMessage(err),
          })
        },
      })
    }
  }

  const isPending = createMutation.isPending || updateMutation.isPending
  const handleFormSubmit = form.onSubmit(handleSubmit)
  useImperativeHandle(ref, () => ({ submit: () => handleFormSubmit() }))

  return (
    <form onSubmit={handleFormSubmit}>
      {(createMutation.isError || updateMutation.isError) && (
        <Alert
          color="red"
          mb="md"
          withCloseButton
          onClose={() => {
            createMutation.reset()
            updateMutation.reset()
          }}
        >
          {createMutation.error?.message || updateMutation.error?.message}
        </Alert>
      )}
      <Paper withBorder p="md" radius="md">
        <Stack gap="md">
          <SimpleGrid cols={{ base: 1, sm: 2 }}>
            <TextInput
              label={t("adminRoles.name")}
              {...form.getInputProps("name")}
              disabled={isEdit}
              autoFocus={!isEdit}
            />
            <TextInput
              label={t("adminRoles.ruleName")}
              placeholder={t("adminRoles.ruleNamePlaceholder")}
              {...form.getInputProps("rule_name")}
            />
          </SimpleGrid>
          <Textarea
            label={t("adminRoles.description")}
            placeholder={t("adminRoles.descriptionPlaceholder")}
            autosize
            minRows={2}
            maxRows={4}
            {...form.getInputProps("description")}
          />
        </Stack>
      </Paper>
    </form>
  )
})

export default RoleFormContent
